import { AfterViewInit, Component, ElementRef, Inject, NgZone, OnDestroy, ViewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { firstValueFrom } from 'rxjs';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { TagViewModel } from '../tenant-setting-tag/tenant-setting-tag.component';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { DEFAULT_TAG_COLOR, tagStyle } from '@utils/tag-color.util';
import Coloris from '@melloware/coloris';

/** ダイアログオプション */
export type DialogOption = {
  updating: boolean;
  tagList?: TagViewModel[];
  target?: TagViewModel;
};

/** フォームグループ */
type TagFormGroup = FormGroup<{
  /** タグ名 */
  tagName: FormControl<string>;
  /** タグID */
  tagId: FormControl<string>;
  /** タグの背景色（HEX形式） */
  color: FormControl<string>;
}>;

@Component({
  selector: 'app-tenant-setting-tag-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
  ],
  templateUrl: './tenant-setting-tag-edit-dialog.component.html',
  styleUrl: './tenant-setting-tag-edit-dialog.component.scss',
})
export class TenantSettingTagEditDialogComponent implements AfterViewInit, OnDestroy {
  /** 一覧と共通のタグ表示スタイルをプレビューにも適用する。 */
  public readonly tagStyle = tagStyle;

  /** 選択肢の並び順を固定したプリセットカラー。 */
  public readonly presetColors = [
    { name: 'Yellow', hex: '#FEF08A' },
    { name: 'Orange', hex: '#FED7AA' },
    { name: 'Red', hex: '#FECACA' },
    { name: 'Pink', hex: '#FBCFE8' },
    { name: 'Purple', hex: '#E9D5FF' },
    { name: 'Violet', hex: '#DDD6FE' },
    { name: 'Blue', hex: '#BFDBFE' },
    { name: 'Sky', hex: '#BAE6FD' },
    { name: 'Cyan', hex: '#A5F3FC' },
    { name: 'Green', hex: '#BBF7D0' },
    { name: 'Lime', hex: '#D9F99D' },
    { name: 'Gray', hex: '#E5E7EB' },
  ];

  /** ＋ボタン内の入力欄をColorisの起点として使う。 */
  @ViewChild('customColorInput') private customColorInput!: ElementRef<HTMLInputElement>;
  /** 更新中 */
  public updating: boolean;

  /** タグ一覧 */
  public tagList: TagViewModel[];

  /** フォームグループ */
  public formGroup: TagFormGroup;

  /** 最後に選択したプリセット外の色 */
  public customColor: string | null;

  constructor(
    @Inject(MAT_DIALOG_DATA) options: DialogOption,
    private dialogRef: MatDialogRef<TenantSettingTagEditDialogComponent>,
    private dialog: MatDialog,
    private zone: NgZone,
  ) {
    this.updating = options.updating;
    this.tagList = options.tagList || [];

    // 新規作成では黄色、編集では保存済みの色をフォームの初期値にする。
    const initialColor = (options.target?.color || DEFAULT_TAG_COLOR).toUpperCase();

    // 保存済みの色がプリセット外なら、任意色のスウォッチを最初から表示する。
    this.customColor = this.presetColors.some(({ hex }) => hex === initialColor)
      ? null
      : initialColor;
    this.formGroup = new FormGroup({
      tagName: new FormControl(options.target?.tagName || '', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(32)],
      }),
      tagId: new FormControl(options.target?.tagId || '', {
        nonNullable: true,
      }),
      color: new FormControl(initialColor, {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(/^#[0-9a-fA-F]{6}$/)],
      }),
    });
  }

  /** Colorisを＋ボタンに接続し、選択色をフォームとスウォッチに反映する。 */
  public ngAfterViewInit(): void {
    Coloris.init();
    Coloris({
      el: this.customColorInput.nativeElement,
      // ダイアログ外のbodyに配置して、ポップアップの表示とクリック操作を妨げない。
      parent: document.body,
      // ＋ボタンの丸いレイアウトをColoris側のラッパーで変えない。
      wrap: false,
      format: 'hex',
      alpha: false,
      onChange: (color) => {
        // Colorisから有効なHEX色が届いた場合だけフォームへ反映する。
        if (/^#[0-9a-fA-F]{6}$/.test(color)) {
          // ライブラリのコールバックからAngularの画面更新を行う。
          this.zone.run(() => {
            const selectedColor = color.toUpperCase();

            // プリセット外の色だけを任意色スウォッチとして保持する。
            if (!this.presetColors.some(({ hex }) => hex === selectedColor)) {
              const firstCustomColor = this.customColor === null;
              this.customColor = selectedColor;
              if (firstCustomColor) {
                // スウォッチ追加で＋ボタンが移動するため、描画後にポップアップを再配置する。
                requestAnimationFrame(() => {
                  if (this.customColorInput.nativeElement.isConnected) {
                    Coloris.updatePosition();
                  }
                });
              }
            }
            this.formGroup.controls.color.setValue(selectedColor);
          });
        }
      },
    });
  }

  /** ダイアログ終了後にColorisの表示とコールバックを解除する。 */
  public ngOnDestroy(): void {
    Coloris.close();
    Coloris({
      el: this.customColorInput.nativeElement,
      parent: document.body,
      onChange: () => {},
    });
  }

  /** プリセット・任意色スウォッチからの選択をフォームとColoris入力欄で揃える。 */
  public selectColor(hex: string): void {
    this.formGroup.controls.color.setValue(hex);
    this.customColorInput.nativeElement.value = hex;
  }

  /** 保存色との大文字・小文字の差を無視して選択状態を判定する。 */
  public isSelected(hex: string): boolean {
    return this.formGroup.controls.color.value.toUpperCase() === hex;
  }

  /**
   * 送信
   */
  public async submit() {
    const tagName = this.formGroup.get('tagName')?.value || '';
    const operation = this.updating ? '変更' : '新規登録';
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `タグ「${tagName}」を${operation}してよろしいですか？`,
      operation: operation,
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    // 色の選択元によらず、同じHEX値としてタグ登録・更新へ渡す。
    const tag: TagViewModel = {
      tagName: this.formGroup.get('tagName')?.value || '',
      tagId: this.formGroup.get('tagId')?.value || '',
      color: this.formGroup.controls.color.value.toUpperCase(),
    };
    this.dialogRef.close(tag);
  }
}
